import React, { useEffect, useRef, useState } from 'react';
import { statsData } from '../data/mock';

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-[#04101C] relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/4 w-[300px] h-[300px] bg-[#00C4CC]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[200px] h-[200px] bg-[#00E08F]/10 rounded-full blur-[80px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className={`text-3xl md:text-4xl font-bold text-[#F5FDFF] mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Why Podcasts <span className="bg-gradient-to-r from-[#00C4CC] to-[#FFF7AE] bg-clip-text text-transparent">Now</span>
          </h2>
          <p
            className={`text-[#F5FDFF]/60 max-w-xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            The podcast industry is booming. Here&apos;s why it&apos;s the perfect time to start.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {statsData.map((stat, index) => (
            <div
              key={index}
              className={`relative p-6 md:p-8 rounded-2xl bg-[#F5FDFF] border border-[#00C4CC]/20 group hover:border-[#00E08F] transition-all duration-500 hover:shadow-[0_0_30px_rgba(0,224,143,0.2)] ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Accent Line */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-1 rounded-full bg-gradient-to-r from-[#00C4CC] to-[#00E08F] group-hover:w-24 transition-all duration-300" />

              {/* Number */}
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#04101C] mb-2">
                <CountUp
                  end={stat.number}
                  isVisible={isVisible}
                  delay={index * 200}
                />
              </div>

              {/* Label */}
              <p className="text-[#04101C]/60 text-sm md:text-base">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CountUp Component
const CountUp = ({ end, isVisible, delay }) => {
  const [count, setCount] = useState('0');
  const numericValue = parseFloat(end.replace(/[^0-9.]/g, ''));
  const suffix = end.replace(/[0-9.]/g, '');

  useEffect(() => {
    if (!isVisible) return;

    const timeout = setTimeout(() => {
      const duration = 2000;
      const steps = 60;
      const increment = numericValue / steps;
      let current = 0;

      const interval = setInterval(() => {
        current += increment;
        if (current >= numericValue) {
          setCount(end);
          clearInterval(interval);
        } else {
          const displayValue = current % 1 === 0 ? Math.floor(current) : current.toFixed(1);
          setCount(`${displayValue}${suffix}`);
        }
      }, duration / steps);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timeout);
  }, [isVisible, numericValue, suffix, end, delay]);

  return <span>{count}</span>;
};

export default StatsSection;
